import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { A2_DISEASE_OTHER_ID } from '@/constants/consentForm';
import type { ConsentFormData } from '@/schemas/consentSchema';
import { ensureArray } from '../utils';

interface UseConsentInjuryNameSyncParams {
  diseaseNameById: Map<string, string>;
  isA2: boolean;
  setValue: UseFormSetValue<ConsentFormData>;
  watch: UseFormWatch<ConsentFormData>;
}

export const useConsentInjuryNameSync = ({
  diseaseNameById,
  isA2,
  setValue,
  watch,
}: UseConsentInjuryNameSyncParams) => {
  const diseaseName = ensureArray(watch('disease_name'));
  const diseaseNameOther = watch('disease_name_other') || '';
  const diseaseNameR2 = watch('disease_name_r2') || '';
  const sameContent = watch('same_content');
  const autoInjuryName = useMemo(() => {
    if (!isA2) return '';
    const selected = Array.isArray(diseaseName) ? diseaseName : [];
    return selected
      .map((token: string) => {
        const id = String(token);
        const name = diseaseNameById.get(id) ?? id;
        if (id !== A2_DISEASE_OTHER_ID) return name;
        const other = String(diseaseNameOther || '').trim();
        return other ? `${name} (${other})` : name;
      })
      .join('、');
  }, [diseaseName, diseaseNameById, diseaseNameOther, isA2]);

  const prevSameContentRef = useRef<boolean | undefined>(undefined);
  const prevDiseaseNameRef = useRef<string | unknown[]>([]);
  const prevDiseaseNameR2Ref = useRef<string>('');
  const prevDiseaseNameOtherRef = useRef<string>('');

  const injuryNameEditedRef = useRef(false);
  const userActionRef = useRef(false);

  useEffect(() => {
    const isUserAction = userActionRef.current;
    userActionRef.current = false;

    const justChecked = sameContent && prevSameContentRef.current === false && isUserAction;
    const justUnchecked = !sameContent && prevSameContentRef.current === true && isUserAction;

    // Detect if Disease Name 1 changed and it was a user action.
    const diseaseNameChanged = isA2
      ? (JSON.stringify(diseaseName) !== JSON.stringify(prevDiseaseNameRef.current) &&
          isUserAction) ||
        (diseaseNameOther !== prevDiseaseNameOtherRef.current && isUserAction)
      : diseaseNameR2 !== prevDiseaseNameR2Ref.current && isUserAction;

    if (justChecked) {
      injuryNameEditedRef.current = false;
      if (isA2) {
        setValue('injury_name', autoInjuryName, { shouldDirty: true });
      } else {
        setValue('injury_name', diseaseNameR2, { shouldDirty: true });
      }
    } else if (sameContent && diseaseNameChanged) {
      if (isA2) {
        setValue('injury_name', autoInjuryName, { shouldDirty: true });
      } else {
        setValue('injury_name', diseaseNameR2, { shouldDirty: true });
      }
    } else if (justUnchecked) {
      injuryNameEditedRef.current = false;
    }

    prevSameContentRef.current = sameContent;
    prevDiseaseNameRef.current = diseaseName;
    prevDiseaseNameR2Ref.current = diseaseNameR2;
    prevDiseaseNameOtherRef.current = diseaseNameOther;
  }, [
    autoInjuryName,
    diseaseName,
    diseaseNameOther,
    diseaseNameR2,
    isA2,
    sameContent,
    setValue,
  ]);

  const markDiseaseUserAction = useCallback(() => {
    userActionRef.current = true;
  }, []);

  const markInjuryNameEdited = useCallback(() => {
    injuryNameEditedRef.current = true;
  }, []);

  return {
    diseaseName,
    markDiseaseUserAction,
    markInjuryNameEdited,
  };
};
